import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "./contexts/ThemeContext";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { Toaster } from "./components/ui/sonner";
import { lazy, Suspense } from "react";
import { DashboardSkeleton, PageSkeleton, TableSkeleton } from "./components/ui/skeletons";

// Eager load - critical path
import { LoginPage } from "./components/pages/LoginPage";
import { SetupPaymentPage } from "./components/pages/SetupPaymentPage";

// Lazy load - all other pages
const ForgotPasswordPage = lazy(() => import("./pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage"));
const DashboardPage = lazy(() => import("./components/pages/DashboardPage").then(m => ({ default: m.DashboardPage })));
const BuildingsPage = lazy(() => import("./components/pages/BuildingsPage").then(m => ({ default: m.BuildingsPage })));
const BuildingDetailPage = lazy(() => import("./components/pages/BuildingDetailPage").then(m => ({ default: m.BuildingDetailPage })));
const UnitDetailPage = lazy(() => import("./components/pages/UnitDetailPage").then(m => ({ default: m.UnitDetailPage })));
const SettlementsPage = lazy(() => import("./components/pages/SettlementsPage").then(m => ({ default: m.SettlementsPage })));
const PaymentsPage = lazy(() => import("./components/pages/PaymentsPage").then(m => ({ default: m.PaymentsPage })));
const SettingsPage = lazy(() => import("./components/pages/SettingsPage").then(m => ({ default: m.SettingsPage })));
const UsersPage = lazy(() => import("./components/pages/UsersPage").then(m => ({ default: m.UsersPage })));
const MyUnitPage = lazy(() => import("./components/pages/MyUnitPage").then(m => ({ default: m.MyUnitPage })));
const PaymentPage = lazy(() => import("./components/pages/PaymentPage").then(m => ({ default: m.PaymentPage })));
const PaymentSuccessPage = lazy(() => import("./components/pages/PaymentSuccessPage").then(m => ({ default: m.PaymentSuccessPage })));
const PaymentCancelPage = lazy(() => import("./components/pages/PaymentCancelPage").then(m => ({ default: m.PaymentCancelPage })));
const DataManagementPage = lazy(() => import("./components/pages/DataManagementPage").then(m => ({ default: m.DataManagementPage })));
const LoxoneInventoryPage = lazy(() => import("./components/pages/LoxoneInventoryPage"));
const AdminDataToolsPage = lazy(() => import("./components/pages/AdminDataToolsPage"));
const AdminPanelPage = lazy(() => import("./components/pages/AdminPanelPage"));
const APIKeysPage = lazy(() => import("./components/pages/APIKeysPage"));
const WebhooksPage = lazy(() => import("./components/pages/WebhooksPage"));
const AdminStudioPage = lazy(() => import("./components/pages/AdminStudioPage"));
const DiagnosticsPage = lazy(() => import("./components/pages/DiagnosticsPage"));
const BackupsPage = lazy(() => import("./components/pages/BackupsPage"));
const PdfEditorPage = lazy(() => import("./components/pages/PdfEditorPage"));
const EmailPortalPage = lazy(() => import("./components/pages/EmailPortalPage"));

// Wallbox EV Pages (lazy)
const WallboxDashboardPage = lazy(() => import("./components/wallbox/WallboxDashboardPage").then(m => ({ default: m.WallboxDashboardPage })));
const WallboxDetailPage = lazy(() => import("./components/wallbox/WallboxDetailPage").then(m => ({ default: m.WallboxDetailPage })));
const WallboxQRPage = lazy(() => import("./components/wallbox/WallboxQRPage").then(m => ({ default: m.WallboxQRPage })));
const PublicChargingPage = lazy(() => import("./components/wallbox/PublicChargingPage").then(m => ({ default: m.PublicChargingPage })));
const LiveChargingDemo = lazy(() => import("./components/wallbox/LiveChargingDemo").then(m => ({ default: m.LiveChargingDemo })));

const LandingPage = lazy(() => import("./components/pages/LandingPage"));
const PresentationPage = lazy(() => import("./components/pages/PresentationPage"));
const NavodPage = lazy(() => import("./components/pages/NavodPage"));
const PublicDiagnosticsPage = lazy(() => import("./components/pages/PublicDiagnosticsPage"));

// Role-based route protection
const ROLE_PERMISSIONS = {
  super_admin: ['dashboard', 'buildings', 'settlements', 'payments', 'settings', 'users', 'my-unit', 'data-management', 'loxone-inventory', 'admin-data-tools', 'wallbox', 'admin-panel', 'api-keys', 'webhooks', 'admin-studio', 'diagnostics', 'backups', 'pdf-editor'],
  landlord: ['dashboard', 'buildings', 'settlements', 'payments', 'my-unit', 'settings', 'wallbox', 'pdf-editor'],
  tenant: ['my-unit'],
  accountant: ['dashboard', 'settlements', 'payments'],
};

// Protected Route wrapper with role check and paywall
const ProtectedRoute = ({ children, requiredRole, moduleKey, bypassPaywall }) => {
  const { user, loading, licenseAccess } = useAuth();

  if (loading) {
    return (
      <div className="loading-screen bg-background">
        <div className="logo-container">
          <span className="energomat-logo text-4xl">
            <span className="energo">energo</span><span className="mat">mat</span>
          </span>
        </div>
        <div className="progress-track bg-muted">
          <div className="progress-fill" />
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Paywall check - redirect to payment setup if no access
  if (!bypassPaywall && licenseAccess && !licenseAccess.has_access) {
    return <Navigate to="/setup-payment" replace />;
  }

  // Check module access for non-admin users
  const userRole = user.role || 'tenant';
  if (moduleKey && userRole !== 'super_admin') {
    const modules = user.modules || {};
    if (!modules[moduleKey]) {
      return <Navigate to="/dashboard" replace />;
    }
  }

  // Check role permissions if required
  if (requiredRole) {
    const allowedRoutes = ROLE_PERMISSIONS[userRole] || [];
    
    if (!allowedRoutes.includes(requiredRole)) {
      if (userRole === 'tenant') {
        return <Navigate to="/my-unit" replace />;
      }
      return <Navigate to="/dashboard" replace />;
    }
  }

  // Force full remount of all children when user changes (prevents stale data across sessions)
  return <div key={user.id}>{children}</div>;
};

// Public Route wrapper (redirect to appropriate page if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="loading-screen bg-background">
        <div className="logo-container">
          <span className="energomat-logo text-4xl">
            <span className="energo">energo</span><span className="mat">mat</span>
          </span>
        </div>
        <div className="progress-track bg-muted">
          <div className="progress-fill" />
        </div>
      </div>
    );
  }

  if (user) {
    // Redirect tenant to my-unit, others to dashboard
    if (user.role === 'tenant') {
      return <Navigate to="/my-unit" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppRoutes() {
  const { user } = useAuth();
  
  // Determine default route based on role
  const getDefaultRoute = () => {
    if (!user) return '/login';
    if (user.role === 'tenant') return '/my-unit';
    return '/dashboard';
  };

  return (
    <Suspense fallback={<div className="loading-screen bg-background"><div className="logo-container"><span className="energomat-logo text-4xl"><span className="energo">energo</span><span className="mat">mat</span></span></div><div className="progress-track bg-muted"><div className="progress-fill" /></div></div>}>
    <Routes>
      {/* Landing page - public */}
      <Route path="/landing" element={<LandingPage />} />
      <Route path="/prezentace" element={<PresentationPage />} />
      <Route path="/navod" element={<NavodPage />} />
      <Route path="/diagnostika" element={<PublicDiagnosticsPage />} />
      
      {/* Public routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <PublicRoute>
            <ForgotPasswordPage />
          </PublicRoute>
        }
      />
      <Route
        path="/reset-password"
        element={
          <PublicRoute>
            <ResetPasswordPage />
          </PublicRoute>
        }
      />

      {/* Protected routes with role checks */}
      <Route
        path="/setup-payment"
        element={
          <ProtectedRoute bypassPaywall>
            <SetupPaymentPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute requiredRole="dashboard">
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/buildings"
        element={
          <ProtectedRoute requiredRole="buildings">
            <BuildingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/buildings/:id"
        element={
          <ProtectedRoute requiredRole="buildings">
            <BuildingDetailPage />
          </ProtectedRoute>
        }
      />
      {/* Unit detail - accessible from building detail page */}
      <Route
        path="/units/:id"
        element={
          <ProtectedRoute requiredRole="buildings">
            <UnitDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-unit"
        element={
          <ProtectedRoute requiredRole="my-unit">
            <MyUnitPage />
          </ProtectedRoute>
        }
      />
      {/* Removed: /renters, /loxone, /fve - now available as tabs in BuildingDetailPage */}
      <Route
        path="/settlements"
        element={
          <ProtectedRoute requiredRole="settlements">
            <SettlementsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/payments"
        element={
          <ProtectedRoute requiredRole="payments">
            <PaymentsPage />
          </ProtectedRoute>
        }
      />
      {/* Removed standalone /loxone and /fve routes - now in BuildingDetailPage tabs */}
      <Route
        path="/settings"
        element={
          <ProtectedRoute requiredRole="settings">
            <SettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/users"
        element={
          <ProtectedRoute requiredRole="users">
            <UsersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/data-management"
        element={
          <ProtectedRoute requiredRole="data-management">
            <DataManagementPage />
          </ProtectedRoute>
        }
      />
      {/* P2-P3 Admin routes */}
      <Route
        path="/loxone-inventory/:buildingId"
        element={
          <ProtectedRoute requiredRole="loxone-inventory">
            <LoxoneInventoryPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/data-tools"
        element={
          <ProtectedRoute requiredRole="admin-data-tools">
            <AdminDataToolsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/panel"
        element={
          <ProtectedRoute requiredRole="admin-panel">
            <AdminPanelPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/api-keys"
        element={
          <ProtectedRoute requiredRole="api-keys">
            <APIKeysPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/webhooks"
        element={
          <ProtectedRoute requiredRole="webhooks">
            <WebhooksPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/studio"
        element={
          <ProtectedRoute requiredRole="admin-studio">
            <AdminStudioPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/diagnostics"
        element={
          <ProtectedRoute requiredRole="diagnostics">
            <DiagnosticsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/backups"
        element={
          <ProtectedRoute requiredRole="backups">
            <BackupsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/pdf-editor"
        element={
          <ProtectedRoute requiredRole="pdf-editor">
            <PdfEditorPage />
          </ProtectedRoute>
        }
      />
      {/* LoxoneMappingStudio route removed - functionality merged into BuildingDetailPage */}

      <Route
        path="/emailing"
        element={
          <ProtectedRoute requiredRole="settings">
            <EmailPortalPage />
          </ProtectedRoute>
        }
      />

      {/* Wallbox EV routes */}
      <Route
        path="/wallbox"
        element={
          <ProtectedRoute requiredRole="wallbox" moduleKey="wallbox">
            <WallboxDashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wallbox/:wallboxId"
        element={
          <ProtectedRoute requiredRole="wallbox" moduleKey="wallbox">
            <WallboxDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wallbox/:wallboxId/qr"
        element={
          <ProtectedRoute requiredRole="wallbox" moduleKey="wallbox">
            <WallboxQRPage />
          </ProtectedRoute>
        }
      />
      {/* Public charging page - no auth required */}
      <Route
        path="/charge/:wallboxId"
        element={<PublicChargingPage />}
      />
      {/* Live charging demo - for showing active session */}
      <Route
        path="/session/:sessionId"
        element={<LiveChargingDemo />}
      />

      {/* Payment routes - accessible to anyone logged in */}
      <Route
        path="/platba/:settlementId"
        element={
          <ProtectedRoute>
            <PaymentPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/payments/success"
        element={
          <ProtectedRoute>
            <PaymentSuccessPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/payments/cancel"
        element={
          <ProtectedRoute>
            <PaymentCancelPage />
          </ProtectedRoute>
        }
      />

      {/* Redirect root based on role */}
      <Route path="/" element={<Navigate to={getDefaultRoute()} replace />} />
      
      {/* Catch all - redirect to appropriate page */}
      <Route path="*" element={<Navigate to={getDefaultRoute()} replace />} />
    </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
